import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { useEffect, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { AppHeader } from 'src/business/components'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { Spinner } from 'react-bootstrap'
import { getListItemsFromWebinarsList, getWebinars } from './webinar-utils'
import { WebinarCard } from './WebinarCard'
import {
  WebinarsPageContainer,
  WebinarsPageTitle,
  WebinarsPageSubtitle,
  WebinarsGrid,
  WebinarsLoadMore,
} from './WebinarPageStyles'

const TAG_MAP = { 0: 'popular', 2: 'trending', 4: 'popular', 5: 'popular', 6: 'trending', 9: 'popular' }

function getTagForIndex(idx) {
  return TAG_MAP[idx] ?? null
}

export function WebinarsListing({ header = 'business' }) {
  const [pageLoading, setPageLoading] = useState(false)
  const [webinarList, setWebinarList] = useState([])
  const [pageNumber, setPageNumber] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [selectedWebinar, setSelectedWebinar] = useState(null)
  const [openWebinarDialog, setOpenWebinarDialog] = useState(false)
  const [webinarRequestRaisingLoading, setWebinarRequestRaisingLoading] = useState(false)
  const [webinarRequestAlreadyRaised, setWebinarRequestAlreadyRaised] = useState(false)

  async function pageSetup() {
    setPageLoading(true)
    const resp = await getWebinars(1)
    if (resp?.status === 200) {
      setWebinarList(resp?.data ?? [])
    }
    setPageLoading(false)
  }

  async function getNextWebinarPage() {
    const nextPageNumber = pageNumber + 1
    const resp = await getWebinars(nextPageNumber)
    if (resp?.status === 200) {
      if (resp?.data?.length > 0) {
        setPageNumber(nextPageNumber)
        setWebinarList((prev) => [...prev, ...resp.data])
      } else {
        setHasNextPage(false)
      }
    } else {
      setHasNextPage(false)
    }
  }

  const [sentryRef] = useInfiniteScroll({
    hasNextPage,
    onLoadMore: getNextWebinarPage,
    rootMargin: '0px 0px 20px 0px',
  })

  function handleWebinarClick(item) {
    setSelectedWebinar(item)
    setOpenWebinarDialog(true)
  }

  function closeWebinarDialog() {
    setOpenWebinarDialog(false)
    setWebinarRequestAlreadyRaised(false)
  }

  const raiseWebinarRequest = async () => {
    setWebinarRequestRaisingLoading(true)
    await sleep(1500)
    setWebinarRequestRaisingLoading(false)
    setWebinarRequestAlreadyRaised(true)
  }

  useEffect(() => {
    pageSetup()
  }, [])

  const listItems = getListItemsFromWebinarsList(webinarList)

  return (
    <>
      <SEOComponent
        title="Webinars | AUMHUM"
        description="Explore Webinars"
        image={APP_CONSTANTS.APP_LOGO_LINK}
      />
      {pageLoading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <>
          {header === 'public' ? (
            <PublicAppHeader config={{ showLinks: true }} />
          ) : (
            <AppHeader heading="Webinars" />
          )}
          <WebinarsPageContainer>
            <WebinarsPageTitle>Explore Webinars</WebinarsPageTitle>
            <WebinarsPageSubtitle>
              Discover sessions on wellness, mental health, and productivity.
            </WebinarsPageSubtitle>
            <WebinarsGrid>
              {listItems?.map((item, idx) => (
                <WebinarCard
                  key={item?.id ?? idx}
                  item={item}
                  tagType={getTagForIndex(idx)}
                  onClick={handleWebinarClick}
                />
              ))}
            </WebinarsGrid>
            {hasNextPage && listItems?.length > 0 && (
              <WebinarsLoadMore ref={sentryRef} />
            )}
          </WebinarsPageContainer>
        </>
      )}
      <Dialog
        open={openWebinarDialog}
        onClose={closeWebinarDialog}
        PaperProps={{
          sx: {
            borderRadius: 16,
            maxWidth: 420,
            boxShadow: '0 24px 48px rgba(32, 7, 105, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 700,
            fontSize: 20,
            color: '#1a0a4e',
            pt: 2.5,
            pb: 1,
          }}
        >
          {selectedWebinar?.title}
        </DialogTitle>
        <DialogContent sx={{ pb: 2.5 }}>
          {!webinarRequestAlreadyRaised ? (
            <button
              type="button"
              onClick={raiseWebinarRequest}
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                fontSize: 14,
                background: 'linear-gradient(135deg, #5f2ec7 0%, #200769 100%)',
                color: 'white',
                padding: '12px 20px',
                borderRadius: 12,
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'opacity 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
            >
              Check Availability
              {webinarRequestRaisingLoading && (
                <Spinner animation="border" variant="light" size="sm" />
              )}
            </button>
          ) : (
            <p
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: 15,
                color: '#5c5c7a',
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              Your success manager will get back to you by 10am tomorrow.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default WebinarsListing
